import React, { Component } from 'react';
import { FlameIcon, CalendarDaysIcon, PenLineIcon, ListChecksIcon, TrophyIcon, RefreshCwIcon, GiftIcon, ArrowRightIcon } from 'lucide-react';
type Bonus = {
  num: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  desc: string;
  value: number;
  hot?: boolean;
};
const bonuses: Bonus[] = [{
  num: '01',
  icon: FlameIcon,
  title: 'Soluzionario Completo',
  desc: 'Soluzioni passo-passo per ogni esercizio. Correggi le verifiche in metà tempo.',
  value: 12,
  hot: true
}, {
  num: '02',
  icon: CalendarDaysIcon,
  title: 'Piano Annuale Express',
  desc: 'Pronto da firmare e consegnare in segreteria. Compatibile con tutti i POF.',
  value: 9
}, {
  num: '03',
  icon: PenLineIcon,
  title: 'Kit Verifiche Lampo',
  desc: 'Fila A e Fila B già pronte per evitare copiature in classe.',
  value: 9
}, {
  num: '04',
  icon: ListChecksIcon,
  title: 'Checklist di Autocorrezione',
  desc: 'Rendi gli alunni autonomi nel verificare il proprio lavoro.',
  value: 5
}, {
  num: '05',
  icon: TrophyIcon,
  title: 'Diplomi "Piccolo Matematico"',
  desc: "Attestati d'onore stampabili per chiudere l'anno alla grande.",
  value: 4
}, {
  num: '06',
  icon: RefreshCwIcon,
  title: 'Kit Recupero Rapido BES/DSA',
  desc: 'Schede facilitate per gli alunni in difficoltà. Inclusione vera, subito.',
  value: 10
}];
const totalValue = bonuses.reduce((s, b) => s + b.value, 0);
export function BonusSection() {
  return <section className="w-full bg-bg-main py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-30">
      {/* Background accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(ellipse, rgba(245,158,11,0.10) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-31" />

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-32">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-33">
          <span className="inline-flex items-center gap-2 text-secondary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-secondary/10 border border-secondary/30 px-4 py-1.5 rounded-full mb-4" data-id="element-34">
            <GiftIcon className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-35" />
            Bonus inclusi
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.1] mb-4" data-id="element-36">
            6 bonus esclusivi
            <br className="hidden sm:block" data-id="element-37" />
            <span className="text-secondary" data-id="element-38"> da € {totalValue}</span>
            <span className="text-text-main" data-id="element-39"> — </span>
            <span className="text-cta-green" data-id="element-40">in regalo.</span>
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-41">
            Non comprerai solo il Kit. Riceverai 6 strumenti extra che, da soli,
            valgono più di quanto paghi oggi.
          </p>
        </div>

        {/* Bonus grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-10" data-id="element-42">
          {bonuses.map((bonus, i) => {
          const Icon = bonus.icon;
          return <article key={i} className="relative bg-bg-alt rounded-2xl border border-border-light p-5 sm:p-6 flex items-start gap-4 hover:border-secondary/50 hover:-translate-y-0.5 transition-all duration-300 group overflow-hidden" data-id="element-43">
                {/* HOT ribbon */}
                {bonus.hot && <span className="absolute -top-px right-5 bg-badge-red text-white text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-b-md" data-id="element-44">
                    🔥 Più richiesto
                  </span>}

                {/* Number + Icon */}
                <div className="flex flex-col items-center gap-2 flex-shrink-0" data-id="element-45">
                  <span className="text-secondary/40 font-black text-xs tracking-widest" data-id="element-46">
                    BONUS {bonus.num}
                  </span>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-secondary/10 border border-secondary/30 flex items-center justify-center group-hover:bg-secondary/20 group-hover:border-secondary/60 transition-colors" data-id="element-47">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" strokeWidth={2.25} data-id="element-48" />
                  </div>
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1" data-id="element-49">
                  <div className="flex items-start justify-between gap-2 mb-1.5" data-id="element-50">
                    <h3 className="text-text-main font-black text-base sm:text-lg leading-tight" data-id="element-51">
                      {bonus.title}
                    </h3>
                  </div>
                  <p className="text-text-sec text-sm sm:text-[15px] leading-snug mb-3" data-id="element-52">
                    {bonus.desc}
                  </p>

                  {/* Price stack: value + GRATIS */}
                  <div className="flex items-center gap-2 pt-3 border-t border-border-light" data-id="element-53">
                    <span className="text-text-sec text-xs sm:text-sm font-medium" data-id="element-54">
                      Valore:
                    </span>
                    <span className="text-text-sec text-sm sm:text-base font-bold line-through" data-id="element-55">
                      € {bonus.value}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1 bg-cta-green/15 text-cta-green border border-cta-green/30 px-2.5 py-1 rounded-md font-black text-xs sm:text-sm" data-id="element-56">
                      ✓ in omaggio
                    </span>
                  </div>
                </div>
              </article>;
        })}
        </div>

        {/* Value Stack Summary */}
        <div className="relative bg-bg-alt border-2 border-secondary/40 rounded-2xl sm:rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(245,158,11,0.15)]" data-id="element-57">
          <div className="absolute inset-0 pointer-events-none opacity-50" style={{
          background: 'radial-gradient(ellipse at top, rgba(245,158,11,0.12) 0%, transparent 70%)'
        }} aria-hidden="true" data-id="element-58" />
          <div className="relative z-10 p-5 sm:p-8" data-id="element-59">
            <p className="text-center text-secondary text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] mb-4 sm:mb-5" data-id="element-60">
              💰 Riepilogo valore bonus
            </p>

            <div className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-5" data-id="element-61">
              {bonuses.map((b, i) => <div key={i} className="flex items-center justify-between text-sm sm:text-base border-b border-border-light/50 pb-1.5 sm:pb-2 last:border-0" data-id="element-62">
                  <span className="text-text-sec font-medium truncate pr-3" data-id="element-63">
                    <span className="text-secondary/70 font-black mr-2 tabular-nums" data-id="element-64">
                      {b.num}
                    </span>
                    {b.title}
                  </span>
                  <span className="text-text-main font-bold flex-shrink-0 tabular-nums" data-id="element-65">
                    € {b.value},00
                  </span>
                </div>)}
            </div>

            <div className="flex items-center justify-between pt-3 sm:pt-4 border-t-2 border-secondary/30" data-id="element-66">
              <span className="text-text-main font-black text-base sm:text-2xl" data-id="element-67">
                Valore totale bonus:
              </span>
              <div className="flex items-center gap-3" data-id="element-68">
                <span className="text-badge-red font-black text-base sm:text-2xl line-through tabular-nums" data-id="element-69">
                  € {totalValue},00
                </span>
                <span className="text-cta-green font-black text-base sm:text-2xl tracking-tight" data-id="element-70">
                  → in omaggio
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 bg-bg-alt border border-border-light rounded-2xl px-6 py-5" data-id="element-71">
          <p className="text-text-main text-base sm:text-lg font-semibold text-center sm:text-left leading-snug" data-id="element-72">
            🎁{' '}
            <span className="text-secondary font-black" data-id="element-73">
              € {totalValue} di bonus
            </span>{' '}
            aggiunti automaticamente al tuo Kit.{' '}
            <span className="text-text-sec" data-id="element-74">Niente codici, niente attese.</span>
          </p>
        </div>
      </div>
    </section>;
}